import { CodedProperty, NamedProperty } from "sprint-common-web";

import { renderBrandedContentVersionText, renderContentVersionText } from "../utils/renderContentVersionText";
import { AncillaryContentSubtype } from "./AncillaryContentType";
import { ContentReferenceType } from "./ContentReferenceType";
import { ContentSubtype } from "./ContentSubtype";
import { MovieContentSubtype } from "./MovieContentSubtype";
import { TvSeriesContentSubtype } from "./TvSeriesContentSubtype";

export type ContentReference = ContentMovieReference | ContentTvSeriesReference | ContentAncillaryReference;

export type ContentTvSeriesReference = PatrialTvSeries | CompleteTvSeries;

export interface ContentMovieReference {
    movie: { id: string; title: string; buzzId?: string };
    versions: ContentMovieVersion[];
    type: ContentReferenceType.Movie;
    subtype: MovieContentSubtype;
}

export interface ContentTvSeriesReferenceBase {
    tvSeries: TvSeries;
    versions: ContentEpisodeVersion[];
    type: ContentReferenceType.TvSeries;
    subtype: TvSeriesContentSubtype;
}

export interface PatrialTvSeries extends ContentTvSeriesReferenceBase {
    season?: Season;
    episode?: Episode;
}

export interface CompleteTvSeries extends ContentTvSeriesReferenceBase {
    season: Season;
    episode: Episode;
}

export interface ContentAncillaryReference {
    ancillary: Ancillary;
    airline: ContentAirline;
    type: ContentReferenceType.Ancillary;
    subtype: AncillaryContentSubtype;
}

interface TvSeries {
    id: string;
    title: string;
    buzzId?: string;
}

interface Season {
    id: string;
    name: string;
    buzzId?: string;
}

interface Episode {
    id: string;
    title: string;
    number: number;
    buzzId?: string;
}

export type ContentTvSeriesSubtype = ContentSubtype.TV;
export type ContentMovieVersion = ContentVersion;
export type ContentEpisodeVersion = ContentVersion;

export interface BaseContentVersion {
    id: string;
    buzzId?: string;
    type: ContentVersionType;
}

export interface ContentVersion extends BaseContentVersion {
    brandings?: CodedProperty[];
    distributor: ContentDistributor;
}

export interface ContentAncillaryVersion extends BaseContentVersion {
    airlines: ContentAirline[];
    distributor?: ContentDistributor;
}

export type ContentAirline = CodedProperty;
export type ContentVersionType = NamedProperty;
export type ContentDistributor = NamedProperty;

export interface Ancillary {
    id: string;
    title: string;
    buzzId?: string;
    version: {
        id: string;
        type: ContentVersionType;
        airlines: ContentAirline[];
        distributor?: ContentDistributor;
        buzzId?: string;
    };
}

export const getContentVersionBuzzIds = (content: ContentReference): string[] => {
    switch (content.type) {
        case ContentReferenceType.Movie:
        case ContentReferenceType.TvSeries: {
            return content.versions.reduce<string[]>(
                (versionIds, version) => (version.buzzId ? [...versionIds, version.buzzId] : versionIds),
                []
            );
        }

        case ContentReferenceType.Ancillary: {
            return content.ancillary.version.buzzId ? [content.ancillary.version.buzzId] : [];
        }

        default:
            throw new Error(`Unsupported content reference type`);
    }
};

export const getContentVersionIds = (contentReference: ContentReference): string[] => {
    switch (contentReference.type) {
        case ContentReferenceType.Movie:
            return contentReference.versions.map((version) => version.id);

        case ContentReferenceType.TvSeries:
            return contentReference.versions.map((version) => version.id);

        case ContentReferenceType.Ancillary:
            return [contentReference.ancillary.version.id];

        default:
            throw new Error("Unsupported content reference type");
    }
};

export const resolveContentTitle = (content: ContentReference): string => {
    switch (content.type) {
        case ContentReferenceType.Movie: {
            return content.movie.title;
        }
        case ContentReferenceType.TvSeries: {
            return resolveTvSeriesTitle(content);
        }
        case ContentReferenceType.Ancillary: {
            return content.ancillary.title;
        }
        default:
            throw new Error("Incorrect Content Type.");
    }
};

export const resolveContentVersion = (content: ContentReference): string[] => {
    switch (content.type) {
        case ContentReferenceType.Movie:
        case ContentReferenceType.TvSeries: {
            return content.versions.map(renderBrandedContentVersionText);
        }

        case ContentReferenceType.Ancillary: {
            return [
                renderContentVersionText({
                    distributor: content.ancillary.version.distributor,
                    type: content.ancillary.version.type,
                    airlines: [content.airline]
                })
            ];
        }

        default:
            throw new Error("Incorrect Content Type.");
    }
};

const resolveTvSeriesTitle = (tvContent: ContentTvSeriesReference) => {
    return [tvContent.tvSeries.title, tvContent.season?.name, tvContent.episode?.title].filter(Boolean).join(" - ");
};
